"use client";

import * as React from "react";
import { cn } from "@/utils/utils";

export interface FormSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Section title */
  title?: string;
  /** Section description */
  description?: string;
  /** Whether to show a divider above the section */
  showDivider?: boolean;
  /** Layout for the section content */
  layout?: "single" | "two-column" | "three-column";
  children: React.ReactNode;
}

/**
 * FormSection groups related form fields with optional title and description.
 * Use it to organize complex forms into logical sections.
 */
export function FormSection({
  className,
  title,
  description,
  showDivider = false,
  layout = "single",
  children,
  ...props
}: FormSectionProps) {
  const layoutClasses = {
    single: "space-y-4 py-4 px-6",
    "two-column": "grid grid-cols-1 md:grid-cols-2 gap-4 py-4 px-6",
    "three-column": "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4 px-6",
  };

  return (
    <div
      className={cn(
        "py-6 first:pt-0 last:pb-0",
        showDivider && "border-t ",
        className
      )}
      {...props}
    >
      {(title || description) && (
        <div className="py-4 px-6 border-b">
          {title && (
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
          )}
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      )}
      <div className={layoutClasses[layout]}>{children}</div>
    </div>
  );
}

FormSection.displayName = "FormSection";

/**
 * FormActions provides a consistent container for form submit/cancel buttons.
 */
export interface FormActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Alignment of the actions */
  align?: "left" | "center" | "right" | "between";
  /** Whether to show a divider above */
  showDivider?: boolean;
  children: React.ReactNode;
}

export function FormActions({
  className,
  align = "center",
  showDivider = true,
  children,
  ...props
}: FormActionsProps) {
  const alignClasses = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
    between: "justify-between",
  };

  return (
    <div
      className={cn(
        "flex items-center gap-3 py-3",
        showDivider && "border-t mt-6",
        alignClasses[align],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

FormActions.displayName = "FormActions";

export default FormSection;
